"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

type LoginResponse = {
  success?: boolean;
  redirectTo?: string;
  error?: string;
};

function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Issue 33.3 — show-password toggle. Defaults to masked.
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json().catch(() => ({}))) as LoginResponse;

      if (!res.ok || data.error) {
        setError(data.error ?? "Something went wrong. Please try again.");
        return;
      }

      router.push(data.redirectTo ?? "/dashboard");
      router.refresh();
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-column">
      <h1 className="auth-headline">Log in.</h1>
      <p className="auth-subline">Welcome back.</p>

      <form onSubmit={handleSubmit} className="auth-panel" noValidate>
        <div className="auth-field">
          <label htmlFor="email" className="auth-label">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            spellCheck={false}
            className="auth-input"
          />
        </div>

        <div className="auth-field">
          <div className="auth-field-row">
            <label htmlFor="password" className="auth-label" style={{ marginBottom: 0 }}>Password</label>
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="auth-link"
              aria-pressed={showPassword}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            placeholder="At least 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="auth-input"
          />
          <div style={{ textAlign: "right" }}>
            <Link href="/forgot-password" className="auth-forgot-link">Forgot password?</Link>
          </div>
        </div>

        {error && (
          <div className="auth-alert" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="auth-submit"
        >
          {submitting ? "Logging in…" : "Log in"}{" "}
          <span aria-hidden="true">→</span>
        </button>

        <div className="auth-switch">
          New here? <Link href="/signup">Create an account</Link>
        </div>
      </form>
    </div>
  );
}

export default function LoginClient() {
  return <LoginForm />;
}
